import React, { useState } from 'react';
import { Search } from 'lucide-react';
import { bcPaymentsService } from '../../services';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { formatGold } from '../../utils/goldNotation';

const STATUS_LABELS = { PENDING: 'Pendente', PAID: 'Pago', DONATED: 'Doado' };
const ROLE_LABELS = { ATAQUE: 'Ataque', BUFF: 'Buff', SUPORTE: 'Suporte' };
// Mesmos rótulos/regras da tela de Pagamento BC.
const PROOF_LABELS = {
  APROVADO: { text: 'Prova aprovada', className: 'bg-green-500/20 text-green-400 border-green-500/30' },
  AGUARDANDO_APROVACAO: { text: 'Aguardando aprovação', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  SEM_PROVA: { text: 'Sem prova', className: 'bg-red-500/20 text-red-400 border-red-500/30' },
  NAO_APLICAVEL: { text: 'Não aplicável', className: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
};
const PROOF_BLOCKS_PAYMENT = ['SEM_PROVA', 'AGUARDANDO_APROVACAO'];

const BcPaymentLookupPage = () => {
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState([]);
  const [totals, setTotals] = useState(null);
  const [lastSearch, setLastSearch] = useState('');
  const [actingId, setActingId] = useState(null);

  const runSearch = async (term) => {
    const data = await bcPaymentsService.lookup(term);
    setResults(data.results);
    setTotals(data.totals);
    setLastSearch(term);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!search.trim()) return;

    try {
      setLoading(true);
      setError('');
      await runSearch(search.trim());
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao buscar pagamentos.');
    } finally {
      setLoading(false);
    }
  };

  // Pagar/Doar/Desfazer usam os mesmos endpoints da tela de Pagamento BC;
  // depois refaz a busca pra atualizar lista e totais.
  const handleAction = async (row, action, fallbackError) => {
    try {
      setActingId(row.beneficiaryId);
      setError('');
      await bcPaymentsService[action](row.bcPaymentId, row.beneficiaryId);
      await runSearch(lastSearch);
    } catch (err) {
      setError(err.response?.data?.error || fallbackError);
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
        <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent mb-4">
          🔎 Consulta de Pagamentos BC
        </h1>
        <form onSubmit={handleSearch} className="flex gap-3">
          <Input
            placeholder="Nome do personagem ou piloto"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            variant="dark"
          />
          <Button type="submit" loading={loading}>
            <Search className="w-4 h-4" /> Buscar
          </Button>
        </form>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-4">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {totals && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
            <p className="text-gray-400 text-sm">Recebido</p>
            <p className="text-2xl font-bold text-green-400" title={Number(totals.received).toLocaleString('pt-BR')}>{formatGold(totals.received)}</p>
          </div>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
            <p className="text-gray-400 text-sm">Doado</p>
            <p className="text-2xl font-bold text-cyan-400" title={Number(totals.donated).toLocaleString('pt-BR')}>{formatGold(totals.donated)}</p>
          </div>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
            <p className="text-gray-400 text-sm">Pendente</p>
            <p className="text-2xl font-bold text-yellow-400" title={Number(totals.pending).toLocaleString('pt-BR')}>{formatGold(totals.pending)}</p>
          </div>
        </div>
      )}

      {results.length > 0 && (
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
          <div className="space-y-2">
            {results.map((r, index) => (
              <div key={`${r.bcPaymentId}-${index}`} className="flex flex-wrap justify-between items-center gap-2 p-3 rounded-xl bg-white/5 border border-white/10">
                <div>
                  <p className="text-white font-medium">{r.specialEventName}</p>
                  <p className="text-gray-400 text-sm">{r.characterName} • {ROLE_LABELS[r.role]}</p>
                  <p className="text-gray-400 text-sm">Piloto: <span className="text-gray-200">{r.recipientName}</span></p>
                </div>
                <p className="text-white font-bold" title={Number(r.shareAmount).toLocaleString('pt-BR')}>{formatGold(r.shareAmount)}</p>
                {r.excluded ? (
                  <span className="text-sm text-gray-500">Excluído do rateio</span>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-gray-300">{STATUS_LABELS[r.status]}</span>
                    {r.status === 'PENDING' ? (
                      <>
                        <span className={`text-xs px-2 py-0.5 rounded-lg border ${(PROOF_LABELS[r.proofStatus] || PROOF_LABELS.NAO_APLICAVEL).className}`}>
                          {(PROOF_LABELS[r.proofStatus] || PROOF_LABELS.NAO_APLICAVEL).text}
                        </span>
                        <span title={PROOF_BLOCKS_PAYMENT.includes(r.proofStatus) ? 'Pagamento liberado só com prova aprovada do próprio piloto no evento vinculado (ou do dono, se não houver piloto)' : undefined}>
                          <Button
                            size="sm"
                            disabled={PROOF_BLOCKS_PAYMENT.includes(r.proofStatus) || actingId !== null}
                            loading={actingId === r.beneficiaryId}
                            onClick={() => handleAction(r, 'payBeneficiary', 'Erro ao marcar como pago.')}
                          >
                            Pagar
                          </Button>
                        </span>
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={actingId !== null}
                          onClick={() => handleAction(r, 'donateBeneficiary', 'Erro ao registrar doação.')}
                        >
                          Doar
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={actingId !== null}
                        loading={actingId === r.beneficiaryId}
                        onClick={() => handleAction(r, 'undoBeneficiary', 'Erro ao desfazer.')}
                      >
                        Desfazer
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BcPaymentLookupPage;
